// /var/www/speakiq/frontend/src/components/AccountMgmtDropdown.js
import React, { useState, useRef, useEffect } from 'react';
import api from '../utils/api';

const C = {
  navy: '#0D1B2A', teal: '#00B4D8', success: '#10B981',
  warning: '#F59E0B', error: '#EF4444', grey: '#6B7280', border: '#E5E7EB',
};

const SUSPEND_REASONS = ['Payment overdue', 'ToS violation', 'Under investigation', 'Partner requested', 'Other'];
const BLOCK_REASONS_PARTNER = ['Serious ToS violation', 'Fraud', 'Repeated non-payment', 'Student complaints', 'Legal requirement', 'Other'];
const BLOCK_REASONS_OTHER = ['Serious behaviour', 'Harassment', 'Fraud', 'Persistent non-payment', 'Other'];

function Overlay({ children, onClose }) {
  useEffect(() => {
    const h = e => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [onClose]);
  return (
    <div onClick={onClose} style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}>
      <div onClick={e => e.stopPropagation()} style={{ background: '#fff', borderRadius: 12, width: '100%', maxWidth: 480, maxHeight: '90vh', overflowY: 'auto', padding: 28, position: 'relative', boxShadow: '0 20px 60px rgba(0,0,0,0.2)' }}>
        <button onClick={onClose} style={{ position: 'absolute', top: 14, right: 14, background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: C.grey }}>×</button>
        {children}
      </div>
    </div>
  );
}

function Toast({ msg, type }) {
  if (!msg) return null;
  const bg = type === 'error' ? '#FEE2E2' : '#D1FAE5';
  const col = type === 'error' ? C.error : C.success;
  return (
    <div style={{ position: 'fixed', bottom: 24, right: 24, background: bg, color: col, padding: '12px 20px', borderRadius: 8, fontWeight: 600, fontSize: 14, zIndex: 2000, boxShadow: '0 4px 12px rgba(0,0,0,0.12)' }}>
      {msg}
    </div>
  );
}

function ModalBtn({ onClick, loading, children, color = C.navy }) {
  return (
    <button onClick={onClick} disabled={loading}
      style={{ background: color, color: '#fff', border: 'none', borderRadius: 8, padding: '10px 22px', fontSize: 14, fontWeight: 600, cursor: loading ? 'not-allowed' : 'pointer', opacity: loading ? 0.7 : 1 }}>
      {loading ? 'Please wait…' : children}
    </button>
  );
}

function ErrorMsg({ msg }) {
  if (!msg) return null;
  return <div style={{ background: '#FEE2E2', color: C.error, padding: '8px 12px', borderRadius: 6, fontSize: 13, marginTop: 10 }}>{msg}</div>;
}

function Label({ children }) {
  return <label style={{ fontSize: 13, fontWeight: 600, color: C.navy, display: 'block', marginBottom: 4 }}>{children}</label>;
}

function Select({ value, onChange, options }) {
  return (
    <select value={value} onChange={e => onChange(e.target.value)}
      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + C.border, fontSize: 14, marginBottom: 12 }}>
      <option value="">— Select reason —</option>
      {options.map(o => <option key={o} value={o}>{o}</option>)}
    </select>
  );
}

function Textarea({ value, onChange, placeholder, rows = 3 }) {
  return (
    <textarea value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} rows={rows}
      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid ' + C.border, fontSize: 14, resize: 'vertical', boxSizing: 'border-box', marginBottom: 12 }} />
  );
}

// ── WARN MODAL ──────────────────────────────────────────────────────────────
function WarnModal({ targetId, targetName, apiBase, onSuccess, onClose }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (!reason.trim()) return setErr('Reason is required.');
    setLoading(true); setErr('');
    try {
      await api.post(`${apiBase}/${targetId}/warn`, { reason });
      onSuccess(`Warning issued to ${targetName}`);
    } catch (e) { setErr(e?.response?.data?.detail || 'Request failed.'); }
    finally { setLoading(false); }
  };
  return (
    <Overlay onClose={onClose}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Issue Warning</h2>
      <p style={{ fontSize: 13, color: C.grey, marginBottom: 16 }}>This will send a formal warning to <strong>{targetName}</strong>.</p>
      <Label>Reason *</Label>
      <Textarea value={reason} onChange={setReason} placeholder="Describe the reason for this warning…" />
      <ErrorMsg msg={err} />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={onClose} style={{ background: 'none', border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
        <ModalBtn onClick={submit} loading={loading} color={C.warning}>Issue Warning</ModalBtn>
      </div>
    </Overlay>
  );
}

// ── SUSPEND MODAL ────────────────────────────────────────────────────────────
function SuspendModal({ targetId, targetName, apiBase, onSuccess, onClose }) {
  const [reason, setReason] = useState('');
  const [indefinite, setIndefinite] = useState(true);
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (!reason) return setErr('Please select a reason.');
    setLoading(true); setErr('');
    try {
      await api.post(`${apiBase}/${targetId}/suspend`, { reason, duration_days: indefinite ? null : Number(days) });
      onSuccess(`${targetName} has been suspended`);
    } catch (e) { setErr(e?.response?.data?.detail || 'Request failed.'); }
    finally { setLoading(false); }
  };
  return (
    <Overlay onClose={onClose}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: C.navy, marginBottom: 4 }}>Suspend Account</h2>
      <p style={{ fontSize: 13, color: C.grey, marginBottom: 16 }}>Suspending <strong>{targetName}</strong>. They will lose access immediately.</p>
      <Label>Reason *</Label>
      <Select value={reason} onChange={setReason} options={SUSPEND_REASONS} />
      <Label>Duration</Label>
      <div style={{ display: 'flex', gap: 16, marginBottom: 12, fontSize: 14 }}>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="radio" checked={indefinite} onChange={() => setIndefinite(true)} /> Indefinite
        </label>
        <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
          <input type="radio" checked={!indefinite} onChange={() => setIndefinite(false)} /> Fixed:
          <input type="number" min={1} value={days} onChange={e => setDays(e.target.value)} disabled={indefinite}
            style={{ width: 60, padding: '4px 6px', border: '1px solid ' + C.border, borderRadius: 5, fontSize: 14 }} /> days
        </label>
      </div>
      <ErrorMsg msg={err} />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={onClose} style={{ background: 'none', border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
        <ModalBtn onClick={submit} loading={loading} color={C.warning}>Suspend Account</ModalBtn>
      </div>
    </Overlay>
  );
}

// ── BLOCK MODAL ──────────────────────────────────────────────────────────────
function BlockModal({ targetType, targetId, targetName, apiBase, onSuccess, onClose }) {
  const reasons = targetType === 'partner' ? BLOCK_REASONS_PARTNER : BLOCK_REASONS_OTHER;
  const confirmPhrase = `BLOCK ${targetName.toUpperCase()}`;
  const [reason, setReason] = useState('');
  const [notes, setNotes] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (!reason) return setErr('Please select a reason.');
    if (confirm !== confirmPhrase) return setErr(`Type exactly: ${confirmPhrase}`);
    setLoading(true); setErr('');
    try {
      await api.post(`${apiBase}/${targetId}/block`, { reason, internal_notes: notes });
      onSuccess(`${targetName} has been blocked`);
    } catch (e) { setErr(e?.response?.data?.detail || 'Request failed.'); }
    finally { setLoading(false); }
  };
  return (
    <Overlay onClose={onClose}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: C.error, marginBottom: 4 }}>Block Account</h2>
      <p style={{ fontSize: 13, color: C.grey, marginBottom: 16 }}>Blocking <strong>{targetName}</strong> will immediately revoke all access.</p>
      <Label>Reason *</Label>
      <Select value={reason} onChange={setReason} options={reasons} />
      <Label>Internal Notes</Label>
      <Textarea value={notes} onChange={setNotes} placeholder="Internal notes (not visible to account holder)…" />
      <Label>Type <strong style={{ fontFamily: 'monospace' }}>{confirmPhrase}</strong> to confirm</Label>
      <input value={confirm} onChange={e => setConfirm(e.target.value)}
        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '2px solid ' + C.error, fontSize: 14, fontFamily: 'monospace', marginBottom: 8, boxSizing: 'border-box' }} />
      <ErrorMsg msg={err} />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={onClose} style={{ background: 'none', border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
        <ModalBtn onClick={submit} loading={loading} color={C.error}>Block Account</ModalBtn>
      </div>
    </Overlay>
  );
}

// ── DELETE MODAL ─────────────────────────────────────────────────────────────
function DeleteModal({ targetType, targetId, targetName, apiBase, onSuccess, onClose }) {
  const confirmPhrase = `DELETE ${targetName.toUpperCase()}`;
  const consequences = targetType === 'partner'
    ? ['All partner data erased after 90 days', 'All student accounts suspended', 'All tutors deactivated', 'Subscription cancelled immediately']
    : ['Account data erased after 30 days', 'All bookings cancelled', 'School ID invalidated', 'Wallet balance forfeited'];
  const [reason, setReason] = useState('');
  const [confirm, setConfirm] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    if (!reason.trim()) return setErr('Reason is required.');
    if (confirm !== confirmPhrase) return setErr(`Type exactly: ${confirmPhrase}`);
    setLoading(true); setErr('');
    try {
      await api.delete(`${apiBase}/${targetId}`, { data: { reason } });
      onSuccess(`${targetName} has been permanently deleted`);
    } catch (e) { setErr(e?.response?.data?.detail || 'Request failed.'); }
    finally { setLoading(false); }
  };
  return (
    <Overlay onClose={onClose}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#7F1D1D', marginBottom: 4 }}>Permanently Delete Account</h2>
      <div style={{ background: '#FEF2F2', border: '1px solid #FECACA', borderRadius: 8, padding: '12px 14px', marginBottom: 16 }}>
        <p style={{ fontWeight: 700, color: '#7F1D1D', fontSize: 13, marginBottom: 6 }}>This action is irreversible. Consequences:</p>
        <ul style={{ paddingLeft: 18, margin: 0 }}>
          {consequences.map(c => <li key={c} style={{ fontSize: 13, color: C.error, marginBottom: 3 }}>{c}</li>)}
        </ul>
      </div>
      <Label>Reason for deletion *</Label>
      <Textarea value={reason} onChange={setReason} placeholder="State the reason for permanent deletion…" />
      <Label>Type <strong style={{ fontFamily: 'monospace' }}>{confirmPhrase}</strong> to confirm</Label>
      <input value={confirm} onChange={e => setConfirm(e.target.value)}
        style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '2px solid #7F1D1D', fontSize: 14, fontFamily: 'monospace', marginBottom: 8, boxSizing: 'border-box' }} />
      <ErrorMsg msg={err} />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={onClose} style={{ background: 'none', border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
        <ModalBtn onClick={submit} loading={loading} color="#7F1D1D">Permanently Delete</ModalBtn>
      </div>
    </Overlay>
  );
}

// ── REINSTATE MODAL ──────────────────────────────────────────────────────────
function ReinstateModal({ targetId, targetName, apiBase, onSuccess, onClose }) {
  const [reason, setReason] = useState('');
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState('');
  const submit = async () => {
    setLoading(true); setErr('');
    try {
      await api.post(`${apiBase}/${targetId}/reinstate`, { reason });
      onSuccess(`${targetName} has been reinstated`);
    } catch (e) { setErr(e?.response?.data?.detail || 'Request failed.'); }
    finally { setLoading(false); }
  };
  return (
    <Overlay onClose={onClose}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: C.success, marginBottom: 4 }}>Reinstate Account</h2>
      <p style={{ fontSize: 13, color: C.grey, marginBottom: 16 }}>Reinstating <strong>{targetName}</strong> will restore full access.</p>
      <Label>Reason (optional)</Label>
      <Textarea value={reason} onChange={setReason} placeholder="Reason for reinstatement…" />
      <ErrorMsg msg={err} />
      <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
        <button onClick={onClose} style={{ background: 'none', border: '1px solid ' + C.border, borderRadius: 8, padding: '10px 18px', cursor: 'pointer', fontSize: 14 }}>Cancel</button>
        <ModalBtn onClick={submit} loading={loading} color={C.success}>Reinstate Account</ModalBtn>
      </div>
    </Overlay>
  );
}

// ── MAIN COMPONENT ────────────────────────────────────────────────────────────
export default function AccountMgmtDropdown({ targetType, targetId, targetName, currentStatus, onActionComplete, apiBase }) {
  const [open, setOpen] = useState(false);
  const [modal, setModal] = useState(null);
  const [toast, setToast] = useState({ msg: '', type: 'success' });
  const ref = useRef(null);

  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const showToast = (msg, type = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast({ msg: '', type: 'success' }), 3500);
  };

  const handleSuccess = msg => {
    setModal(null);
    showToast(msg, 'success');
    onActionComplete && onActionComplete();
  };

  const opts = [];
  if (currentStatus === 'active') {
    if (targetType === 'partner') opts.push({ label: 'Warn', action: 'warn', color: C.warning });
    opts.push({ label: 'Suspend', action: 'suspend', color: C.warning });
    opts.push({ label: 'Block', action: 'block', color: C.error });
    opts.push({ label: 'Delete', action: 'delete', color: '#7F1D1D' });
  } else if (currentStatus === 'suspended' || currentStatus === 'on_hold') {
    opts.push({ label: 'Reinstate', action: 'reinstate', color: C.success });
    opts.push({ label: 'Block', action: 'block', color: C.error });
    opts.push({ label: 'Delete', action: 'delete', color: '#7F1D1D' });
  } else if (currentStatus === 'blocked') {
    opts.push({ label: 'Reinstate', action: 'reinstate', color: C.success });
    opts.push({ label: 'Delete', action: 'delete', color: '#7F1D1D' });
  }

  const commonProps = { targetId, targetName, apiBase, onSuccess: handleSuccess, onClose: () => setModal(null) };

  return (
    <div ref={ref} style={{ position: 'relative', display: 'inline-block' }}>
      <button onClick={() => setOpen(o => !o)}
        style={{ background: '#F3F4F6', border: '1px solid ' + C.border, borderRadius: 6, padding: '6px 14px', fontSize: 13, fontWeight: 600, cursor: 'pointer', color: C.navy }}>
        Actions ▾
      </button>
      {open && (
        <div style={{ position: 'absolute', right: 0, top: '110%', background: '#fff', border: '1px solid ' + C.border, borderRadius: 8, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', zIndex: 100, minWidth: 140, overflow: 'hidden' }}>
          {opts.map(o => (
            <button key={o.action} onClick={() => { setModal(o.action); setOpen(false); }}
              style={{ display: 'block', width: '100%', textAlign: 'left', padding: '10px 16px', fontSize: 13, fontWeight: 600, color: o.color, background: 'none', border: 'none', cursor: 'pointer', borderBottom: '1px solid ' + C.border }}>
              {o.label}
            </button>
          ))}
          {opts.length === 0 && (
            <div style={{ padding: '10px 16px', fontSize: 13, color: C.grey }}>No actions available</div>
          )}
        </div>
      )}

      {modal === 'warn' && <WarnModal {...commonProps} />}
      {modal === 'suspend' && <SuspendModal {...commonProps} />}
      {modal === 'block' && <BlockModal {...commonProps} targetType={targetType} />}
      {modal === 'delete' && <DeleteModal {...commonProps} targetType={targetType} />}
      {modal === 'reinstate' && <ReinstateModal {...commonProps} />}

      <Toast msg={toast.msg} type={toast.type} />
    </div>
  );
}
